import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { NotificationModel } from "./models/NotificationModel";
import { getNotifications, getUnreadNotifies, markAllNotifies } from "../services/notificationService";

export interface NotificationContext {
    notifications: NotificationModel[];
    unread: number;
    loadingList: boolean;
    loadingBadge: boolean;
    loadUnread: () => void;
    loadNotifications: () => void;
}

const NotificationCtx = createContext<NotificationContext | undefined>(undefined);

export const NotificationCtxProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<NotificationModel[]>([]);
    const [unread, setUnread] = useState<number>(0);
    const [loadingList, setLoadingList] = useState(false);
    const [loadingBadge, setLoadingBadge] = useState(false);

    const loadUnread = useCallback(() => {
        let alive = true;
        (async () => {
            setLoadingBadge(true);
            try {
                const _count = await getUnreadNotifies();
                if(!alive)
                    return;
                setUnread(_count.unreadCount);
            }
            finally {
                if(!alive)
                    return;
                setLoadingBadge(false);
            }
        })();
        return () => {
            alive = false;
        }
    }, []);
    
    const loadNotifications = useCallback(() => {
        let alive = true;
        (async () => {
            setLoadingList(true);
            try {
                const data = await getNotifications();
                if(!alive)
                    return;
                setNotifications(data);
                if(!!data && data.some((n: any) => !n.isRead)) {
                    await markAllNotifies();
                }
            }
            finally {
                if(alive)
                    setLoadingList(false);
            }
        })();
        return () => {
            alive = false;
        }
    }, []);

    const value: NotificationContext = {
        notifications,
        unread,
        loadingBadge,
        loadingList,
        loadNotifications,
        loadUnread
    };

    return (
        <NotificationCtx.Provider value={value}>
            {children}
        </NotificationCtx.Provider>
    )
}

export const useNotifications = () => {
    const ctx = useContext(NotificationCtx);
    if(!ctx)
        throw new Error('useNotifications debe usarse dentro de NotificationCtxProvider')
    return ctx;
}